import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import B2BProductList from '@/components/B2BComponents/B2BProductProductList';
import B2BLoadingSkeletonProductList from '@/components/B2BComponents/LoadingSkeletonB2BProductList';
import { HaggleStoredFilters } from '@/components/filters/HaggleStoredFilters';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { useUser } from '@/hooks/useUser';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { HaggleInvite } from '@/interfaces/api/haggle-invite/haggleInvite';
import { INotifications } from '@/interfaces/api/notifications/INotifications';
import { ICollectionProduct } from '@/interfaces/api/product/ICollectionProduct';
import { ProductCardType } from '@/interfaces/notifications/Notifications';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const B2BMarketplace = () => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { state } = useLocation();
	const { vendor, haggleId } = state || {};

	const {
		data: products,
		isLoading: productsIsLoading,
		isError: productIsError,
		error: productError,
	} = useQuery<ICollectionProduct>({
		queryKey: [
			ApiQueryKeys.getAllHaggleProducts,
			pageSize,
			pageNumber,
			searchParams.toString(),
			haggleId,
		],
		queryFn: () =>
			haggleApiService.getAllHaggleProducts({
				params: {
					'page[size]': pageSize,
					'page[number]': pageNumber,
					'filterQuery[haggleEventId]': haggleId,
					'filterQuery[productType]': searchParams.getAll('type'),
					'filterQuery[countryName]': searchParams.getAll('country'),
					'filterQuery[minPrice]': searchParams.get('minPrice')
						? Number(searchParams.get('minPrice'))
						: undefined,
					'filterQuery[maxPrice]': searchParams.get('maxPrice')
						? Number(searchParams.get('maxPrice'))
						: undefined,
					'filterQuery[status]': searchParams.get('status') || undefined,
					'filterQuery[showPublicEventsOnly]': 'b2b',
				},
			}),
		refetchOnWindowFocus: false,
	});

	const { user } = useUser();

	const {
		data: favouriteNotifications,
		isSuccess: isFavouritesLoaded,
		refetch: refetchFavourites,
	} = useQuery<INotifications[]>({
		queryKey: [ApiQueryKeys.getFavouritedNotifications],
		queryFn: () => haggleApiService.getFavouritedNotifications(),
		enabled: !!user,
		refetchOnWindowFocus: false,
	});

	const {
		data: userInvites,
		isSuccess: isUserInvitesLoaded,
		refetch: refetchUserInvites,
	} = useQuery<HaggleInvite[]>({
		queryKey: [ApiQueryKeys.getInvitesByEmail, user?.username],
		queryFn: () => haggleApiService.getHaggleInvitesByEmail(user!.username),
		enabled: !!user?.username,
		refetchOnWindowFocus: false,
	});

	if (productIsError) {
		if (productError instanceof Error) {
			notificationService.error(productError.message);
		} else {
			notificationService.error('Unknown error fetching products');
		}
	}

	const handlePaginationOptions = (
		collection: ICollectionProduct,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	useEffect(() => {
		if (!vendor && !haggleId) {
			navigate('/user/b2b-vendors');
		}
	}, [vendor, haggleId, navigate]);

	return (
		<div className="flex flex-col ">
			<div className="mb-2">
				<HaggleStoredFilters restrictVendors={vendor ? true : false} />
			</div>

			<div className="flex-1">
				{productsIsLoading && <B2BLoadingSkeletonProductList />}
				{products && !productsIsLoading && (
					<B2BProductList
						type={ProductCardType.Home}
						collectionProduct={products}
						paginationOption={handlePaginationOptions(products)}
						favouriteMerchants={
							isFavouritesLoaded ? favouriteNotifications : []
						}
						refetchFavourites={refetchFavourites}
						invitedUsers={isUserInvitesLoaded ? userInvites : []}
						refetchUserInvites={refetchUserInvites}
					/>
				)}
			</div>
		</div>
	);
};

export default B2BMarketplace;
